/**
 * Premium UI primitives — Apple/Linear-inspired.
 * Restricted to brand palette: #A855F7 / #111111 / #FFFFFF / slate.
 * Use these instead of building bespoke styles per page.
 */
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

// ── PremiumButton ────────────────────────────────────────────────────────
export function PremiumButton({
  variant = "primary", // primary | secondary | ghost | dark
  size = "md",         // sm | md | lg
  loading = false,
  icon: Icon,
  iconRight: IconRight,
  children,
  className = "",
  disabled,
  ...rest
}) {
  const sizes = {
    sm: "px-3.5 py-1.5 text-xs",
    md: "px-5 py-2.5 text-sm",
    lg: "px-7 py-3.5 text-base",
  };
  const variants = {
    primary: "bg-[#A855F7] hover:bg-[#9333EA] text-white shadow-soft hover:shadow-glow",
    secondary: "bg-white hover:bg-slate-50 text-[#111111] border border-slate-200 shadow-soft",
    ghost: "bg-transparent hover:bg-slate-100 text-[#111111]",
    dark: "bg-[#111111] hover:bg-[#1f1f1f] text-white shadow-soft",
  };
  return (
    <motion.button
      whileHover={{ y: -1 }}
      whileTap={{ y: 0, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 420, damping: 22 }}
      disabled={loading || disabled}
      className={`inline-flex items-center gap-2 rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-sans ${sizes[size]} ${variants[variant]} ${className}`}
      {...rest}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : Icon ? <Icon className="w-4 h-4"/> : null}
      {children}
      {IconRight && <IconRight className="w-4 h-4"/>}
    </motion.button>
  );
}

// ── PremiumCard ──────────────────────────────────────────────────────────
export function PremiumCard({
  hover = true,
  interactive = false,
  className = "",
  children,
  ...rest
}) {
  return (
    <motion.div
      whileHover={hover ? { y: -3 } : undefined}
      transition={{ type: "spring", stiffness: 320, damping: 22 }}
      className={`bg-white border border-slate-200/80 rounded-2xl shadow-soft ${interactive ? "cursor-pointer" : ""} ${className}`}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// ── SectionHeading ───────────────────────────────────────────────────────
export function SectionHeading({ eyebrow, title, subtitle, align = "left", className = "" }) {
  return (
    <div className={`${align === "center" ? "text-center mx-auto" : ""} ${className}`}>
      {eyebrow && (
        <span className="inline-block text-[11px] tracking-[0.18em] uppercase font-bold text-[#A855F7] mb-3 font-sans">
          {eyebrow}
        </span>
      )}
      <h2 className="font-heading text-4xl sm:text-5xl font-semibold text-[#111111] tracking-tight leading-[1.05]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base sm:text-lg text-slate-600 font-sans max-w-2xl">
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ── EmptyState ───────────────────────────────────────────────────────────
export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-brand-50 text-[#A855F7] flex items-center justify-center mb-4">
          <Icon className="w-6 h-6"/>
        </div>
      )}
      <h3 className="font-heading text-xl font-semibold text-[#111111]">{title}</h3>
      {subtitle && <p className="mt-2 text-sm text-slate-500 max-w-md font-sans">{subtitle}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

// ── Pill ─────────────────────────────────────────────────────────────────
export function Pill({ children, variant = "neutral", icon: Icon, className = "" }) {
  const variants = {
    neutral: "bg-slate-100 text-slate-700",
    brand: "bg-brand-50 text-[#7E22CE]",
    dark: "bg-[#111111] text-white",
    outline: "border border-slate-200 text-slate-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold font-sans ${variants[variant]} ${className}`}>
      {Icon && <Icon className="w-3 h-3"/>}
      {children}
    </span>
  );
}
